import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import BootstrapTable from "react-bootstrap-table-next";
import { Card } from "../../../components/Cards/Card";
import { ExpandableContent } from "../../../components/CustomControls/ExpandableContent";
import { Row, Col, Modal } from "react-bootstrap";
import { CustomSwitch } from "../../../components/CustomControls/CustomSwitch";
import DocumentUploadCard from "../../../components/CustomControls/DocumentUpload/DocumentUploadCard";
import { CustomRadio } from "../../../components/CustomControls/CustomRadio";
import { AcceptState } from "./AcceptState";
import DiscardedState from "./DiscardedState";
import { useTranslation } from "react-i18next";
import ActivateCenter from "./ActivateCenter";
import { useClinicalTrialContext } from "../ClinicalTrialContext";
import {
  attachmentCategories,
  attachmentEntityType,
  conceptType,
  snomedExpressions,
} from "../../../variables/Enums";
import { CustomSelect } from "../../../components/CustomControls/CustomSelect";
import { useStudyRoles } from "../../Security/Authorization/useStudyRoles";
import {
  getAttachmentEndpoint,
  getClinicalTrialCompetitors,
  getPractitionerAttachments,
} from "../../../api/clinicalTrial";
import { Spinner } from "../../../components/Utils/Loaders";
import { Tooltip } from "../../../components/Utils/Tooltips";
import { Disabled, Next } from "../../../components/Icons/Icons";
import { DocumentViewer } from "../../../components/CustomControls/DocViewer/DocumentViewer";
import { dateFormatter } from "../../../components/Utils/Formatter";
import { useFiles } from "../../../components/CustomControls/Files/Hooks";
import { PractitionerPicker } from "../../../components/CustomControls/PractitionerPicker";
import { ModalResponsibleDuplicated } from "../../../components/ClinicalTrials/ModalResponsibleDuplicated";
import TrialStatusHistory from "./TrialStatusHistory";
import { SnomedPicker } from "../../../components/CustomControls/SnomedPicker";
import { CustomDate } from "../../../components/CustomControls/CustomDateTime";
import { useAuthorization } from "../../../components/Authorization/useAuthorization";

export const General = ({
  onSaveStartup,
  onChangeStatus,
  showActivateCenterPanel,
  setShowActivateCenterPanel,
  isProtocolActivated,
}) => {
  const { t } = useTranslation();
  const { startup, setStartup } = useClinicalTrialContext();
  const [, user, isAuthorized] = useAuthorization();
  const [studyRoles] = useStudyRoles();

  const [trialTypeOptions, setTrialTypeOptions] = useState([
    {
      value: t("clinicalTrials.general.trialTypeOptions.observational"),
      label: t("clinicalTrials.general.trialTypeOptions.observational"),
    },
    {
      value: t("clinicalTrials.general.trialTypeOptions.nonInterventional"),
      label: t("clinicalTrials.general.trialTypeOptions.nonInterventional"),
    },
    {
      value: t("clinicalTrials.general.trialTypeOptions.researchProject"),
      label: t("clinicalTrials.general.trialTypeOptions.researchProject"),
    },
    {
      value: t("clinicalTrials.general.trialTypeOptions.interventionalTrial"),
      label: t("clinicalTrials.general.trialTypeOptions.interventionalTrial"),
    },
  ]);
  const [showDuplicatedModal, setShowDuplicatedModal] = useState(false);

  const defaultVisitType = {
    value: t("clinicalTrials.visitType.remote"),
    label: t("clinicalTrials.visitType.remote"),
  };

  const [visitTypeSelectedOption, setVisitTypeSelectedOption] =
    useState(defaultVisitType);

  const [showCompetitors, setShowCompetitors] = useState(false);
  const [clinicalTrialsCompetitors, setClinicalTrialsCompetitors] = useState();
  const [response, downloadBlob] = useFiles(
    getAttachmentEndpoint(attachmentEntityType.Practitioner)
  );
  const [practitionerAttachments, setPractitionerAttachments] = useState();
  const isResponsableEditable = startup?.protocol?.status?.code === "Proposal";
  const isMonitor = user?.sub === startup?.protocol?.monitor?.userId;
  const optionsSelection = [
    {
      value: t("clinicalTrials.visitType.remote"),
      label: t("clinicalTrials.visitType.remote"),
    },
    {
      value: t("clinicalTrials.visitType.onSite"),
      label: t("clinicalTrials.visitType.onSite"),
    },
  ];

  const competitorsTableColumns = [
    {
      dataField: "sponsor",
      text: t("clinicalTrials.startup.sponsor"),
      headerStyle: { width: "100px" },
    },
    {
      dataField: "title",
      text: t("clinicalTrials.startup.acronym"),
      headerStyle: { width: "200px" },
    },
    {
      dataField: "protocolNumber",
      text: t("clinicalTrials.startup.protocolNumber"),
      headerStyle: { width: "100px" },
    },
    {
      dataField: "centerNumber",
      text: t("clinicalTrials.startup.centerNumber"),
      sort: true,
      headerStyle: { width: "100px" },
    },
    {
      dataField: "status",
      text: t("clinicalTrials.startup.status"),
      headerStyle: { width: "100px" },
    },
    {
      dataField: "version",
      text: t("clinicalTrials.startup.version"),
      headerStyle: { width: "50px" },
    },
    {
      dataField: "view",
      text: "",
      isDummyField: true,
      headerClasses: "header-protocol-actions",
      classes: "row-protocol-actions",
      headerStyle: { width: "100px" },
      headerAlign: "center",
      align: "center",
      formatter: (_cell, row, rowIndex) => (
        <div key={rowIndex}>
          {!!row.belongs && (
            <Tooltip
              tooltip={t("clinicalTrials.startup.competitiveViewStartup")}
              id={`tooltipvisit-${row.id}`}
            >
              <NavLink
                to={`/admin/clinicalTrials/edit/${row.id}`}
                activeClassName="active"
                className="btn-table-actions"
              >
                <Next />
              </NavLink>
            </Tooltip>
          )}
          {!row.belongs && (
            <Tooltip
              tooltip={t(
                "clinicalTrials.startup.competitiveViewNotBelongStartup"
              )}
              id={`tooltipvisit-${row.id}`}
            >
              <Disabled />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (!startup) {
      return;
    }

    if (!!startup?.visitType) {
      setVisitTypeSelectedOption({
        value: startup?.visitType,
        label: startup?.visitType,
      });
    }

    if (!trialTypeOptions.some((x) => x.label === startup?.trialDetailsType)) {
      const newTrialTypeOptions = trialTypeOptions;
      newTrialTypeOptions.push({
        label: startup?.trialDetailsType,
        value: startup?.trialDetailsType,
      });
      setTrialTypeOptions(newTrialTypeOptions);
    }

    // Cargar documentos de todos los responsables en cualquier estado
    const loadPractitionerAttachments = async () => {
      const practitioners = [
        startup?.protocol?.principalInvestigator,
        startup?.protocol?.subInvestigator,
        startup?.protocol?.studyCoordinator,
        startup?.protocol?.dataManager,
        startup?.protocol?.pharmacist,
        startup?.protocol?.nurcing,
        startup?.protocol?.administrator,
        startup?.protocol?.monitor,
      ].filter(Boolean); // Filtrar valores nulos/undefined

      // Cargar todos los archivos primero en un objeto temporal
      const newPractitionerAttachments = practitionerAttachments
        ? { ...practitionerAttachments }
        : {};

      // Cargar archivos de todos los responsables
      const loadPromises = practitioners
        .filter((p) => p?.userId && !newPractitionerAttachments[p.userId])
        .map(async (practitioner) => {
          try {
            const response = await getPractitionerAttachments(practitioner.userId);
            const result = response.data;
            const processedAttachments = result.map((attachment) => {
              const extendedMetadata = JSON.parse(attachment.extendedMetadata);

              if (extendedMetadata?.upperFields?.length > 0) {
                var rawDate = extendedMetadata.upperFields[0].value;
                if (rawDate) {
                  if (typeof rawDate === 'string' && rawDate.includes('T')) {
                    attachment.dueDate = new Date(rawDate);
                  } else if (typeof rawDate === 'string' && rawDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    const [year, month, day] = rawDate.split("-").map(Number);
                    attachment.dueDate = new Date(year, month - 1, day);
                  } else {
                    attachment.dueDate = new Date(rawDate);
                  }
                }
              }

              return attachment;
            });

            newPractitionerAttachments[practitioner.userId] = processedAttachments;
          } catch (error) {
            console.error(`Error loading attachments for practitioner ${practitioner.userId}:`, error);
          }
        });

      // Esperar a que todos los archivos se carguen antes de actualizar el estado
      await Promise.all(loadPromises);
      
      // Actualizar el estado una sola vez con todos los archivos cargados
      if (Object.keys(newPractitionerAttachments).length > 0) {
        setPractitionerAttachments(newPractitionerAttachments);
      }
    };

    loadPractitionerAttachments();
  }, [startup]);

  useEffect(() => {
    const loadCompetitors = async () => {
      const response = await getClinicalTrialCompetitors(startup.id);
      if (response.status === 200) {
        setClinicalTrialsCompetitors(response.data.items);
      }
    };

    if (!showCompetitors) {
      setClinicalTrialsCompetitors(undefined);
      return;
    }

    loadCompetitors();
  }, [showCompetitors]);

  const handleStatusChange = (name, value) => {
    if (typeof name === "string" && typeof value === "string") {
      onChangeStatus && onChangeStatus(value);
      setStartup((prev) => ({
        ...prev,
        protocol: {
          ...prev.protocol,
          status: {
            ...prev.protocol.status,
            code: value,
          },
        },
      }));
    }
  };

  const handleActivateCenter = () => {
    handleStatusChange("status", "Recruiting");
  };

  const setFieldValue = (field, value) => {
    setStartup((prev) => ({
      ...prev,
      protocol: {
        ...prev.protocol,
        [field]: value,
      },
    }));
  };

  const getSwitchValue = (name) => {
    switch (name) {
      case "is-competitive":
        return startup?.competitive || false;
      case "is-pre-selection":
        return startup?.preselectionStage || false;
      case "is-selection":
        return startup?.selectionStage || false;
      default:
        throw new Error("Invalid switch");
    }
  };

  const handleSwitchChange = (name, value) => {
    switch (name) {
      case "is-competitive":
        handleStartupChange("competitive", value);
        break;
      case "is-pre-selection":
        handleStartupChange("preselectionStage", value);
        break;
      case "is-selection":
        handleStartupChange("selectionStage", value);
        break;
      default:
        throw new Error("Invalid switch");
    }
  };

  const handleAttachmentChange = (attachment) => {
    setStartup((prev) => ({
      ...prev,
      attachments: prev.attachments.map((currentAttachment) =>
        currentAttachment.attachmentCategoryId ===
        attachment.attachmentCategoryId
          ? { ...currentAttachment, ...attachment }
          : currentAttachment
      ),
    }));
  };

  const handleStartupChange = (key, value) => {
    setStartup((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleProtocolChange = (key, value) => {
    setStartup((prev) => ({
      ...prev,
      protocol: {
        ...prev.protocol,
        [key]: value,
      },
    }));
  };

  const setPractitioner = async (value) => {
    const newPractitionerAttachments = practitionerAttachments
      ? { ...practitionerAttachments }
      : {};

    if (value?.userId) {
      const response = await getPractitionerAttachments(value.userId);

      const result = response.data;
      const processedAttachments = result.map((attachment) => {
        const extendedMetadata = JSON.parse(attachment.extendedMetadata);

        if (extendedMetadata?.upperFields?.length > 0) {
          // La metadata de practitioners contiene un campo de vencimiento
          // asumo que ese primer campo es siempre vencimiento
          var rawDate = extendedMetadata.upperFields[0].value;
          if (rawDate) {
            // La fecha puede venir en formato ISO (2026-02-20T03:00:00.000Z) o yyyy-mm-dd
            if (typeof rawDate === 'string' && rawDate.includes('T')) {
              // Formato ISO completo
              attachment.dueDate = new Date(rawDate);
            } else if (typeof rawDate === 'string' && rawDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
              // Formato yyyy-mm-dd
              const [year, month, day] = rawDate.split("-").map(Number);
              attachment.dueDate = new Date(year, month - 1, day);
            } else {
              // Intentar parsear directamente
              attachment.dueDate = new Date(rawDate);
            }
          }
        }

        return attachment;
      });

      newPractitionerAttachments[value.userId] = processedAttachments;
      setPractitionerAttachments(newPractitionerAttachments);
    }
  };

  const handlePractitionerChange = async (key, value) => {
    // Asegurar que value sea un array para manejar tanto objetos simples como arrays
    const practitionersArray = Array.isArray(value) ? value : (value ? [value] : []);
    
    if (practitionersArray.length > 0) {
      const selectedPractitioner = practitionersArray[0];
      if (isPractitionerAlreadySelected(selectedPractitioner.userId, key)) {
        setShowDuplicatedModal(true);
        return;
      }
      setPractitioner(selectedPractitioner);
      setFieldValue(key, selectedPractitioner);
      return;
    }

    setFieldValue(key, undefined);
  };

  const isPractitionerAlreadySelected = (userId, currentField) => {
    const roles = [
      "subInvestigator",
      "studyCoordinator",
      "dataManager",
      "pharmacist",
      "nurcing",
      "administrator",
      "monitor",
    ];
    return roles.some((role) => {
      if (role === currentField) return false; // ignorar el campo actual
      return startup?.protocol?.[role]?.userId === userId;
    });
  };

  const handlePreviewFile = async (attachmentId) => {
    await downloadBlob(attachmentId);
  };

  // Muestra solo el adjunto más reciente por categoría (evita duplicados cuando se carga un nuevo CV)
  const getLatestAttachmentsByCategory = (attachments) => {
    if (!attachments || !Array.isArray(attachments)) return [];
    const byCategory = {};
    attachments.forEach((att) => {
      const key = att.attachmentCategoryId ?? att.attachmentCategoryName ?? att.id;
      if (!byCategory[key] || (att.id > byCategory[key].id)) {
        byCategory[key] = att;
      }
    });
    return Object.values(byCategory);
  };

  const getAttachmentDueDateStatus = (dueDate) => {
    if (!dueDate) {
      return (
        <span style={{ color: "red", marginLeft: "5px" }}>
          {t("clinicalTrials.general.documentStatuses.notDefined")}
        </span>
      );
    }

    const formattedDate = dateFormatter(dueDate);

    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const startOfDue = new Date(
      dueDate.getFullYear(),
      dueDate.getMonth(),
      dueDate.getDate()
    );

    if (startOfDue < startOfToday) {
      return (
        <span style={{ color: "red", marginLeft: "5px" }}>
          {formattedDate} (
          {t("clinicalTrials.general.documentStatuses.expired")})
        </span>
      );
    }

    if (startOfDue.getTime() === startOfToday.getTime()) {
      return (
        <span style={{ color: "green", marginLeft: "5px" }}>{formattedDate}</span>
      );
    }

    if (
      startOfDue.getMonth() === startOfToday.getMonth() &&
      startOfDue.getFullYear() === startOfToday.getFullYear()
    ) {
      return (
        <span style={{ color: "rgb(255 205 50)", marginLeft: "5px" }}>
          {formattedDate} (
          {t("clinicalTrials.general.documentStatuses.toExpire")})
        </span>
      );
    }

    return (
      <span style={{ color: "green", marginLeft: "5px" }}>{formattedDate}</span>
    );
  };

  return (
    <Row>
      <Col xs={12} className="mb-3">
        <Card
          title={t("clinicalTrials.general.statusChangesHistoryLabel")}
          content={<TrialStatusHistory items={startup?.statusHistory || []} />}
        />
      </Col>
      <Col
        md={4}
        style={{
          position: "sticky",
          top: "0",
          alignSelf: "flex-start",
          zIndex: 1,
        }}
      >
        <div>
          <Card
            title={
              <>
                <i className="fa fa-cogs me-2" />{" "}
                {t("clinicalTrials.general.GeneralInfoLabel")}
              </>
            }
            content={
              <>
                <ExpandableContent title="Identificación" content="user">
                  <div style={{ marginTop: "16px" }}>
                    <strong>{t("clinicalTrials.startup.acronym")}:</strong>
                    <input
                      className="form-control"
                      value={startup?.protocol?.title || ""}
                      onChange={(e) =>
                        handleProtocolChange("title", e.target.value)
                      }
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong>{t("clinicalTrials.startup.title")}:</strong>
                    <input
                      className="form-control"
                      value={startup?.protocol?.fullTitle || ""}
                      onChange={(e) =>
                        handleProtocolChange("fullTitle", e.target.value)
                      }
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong>
                      {t("clinicalTrials.startup.protocolNumber")}:
                    </strong>
                    <input
                      className="form-control"
                      value={startup?.protocol?.protocolNumber || ""}
                      onChange={(e) =>
                        handleProtocolChange("protocolNumber", e.target.value)
                      }
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong>{t("clinicalTrials.startup.ntc")}:</strong>
                    <input
                      className="form-control"
                      value={startup?.ntc || ""}
                      onChange={(e) =>
                        handleStartupChange("ntc", e.target.value)
                      }
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong>{t("clinicalTrials.startup.eudraTc")}:</strong>
                    <input
                      className="form-control"
                      value={startup?.eudraTc || ""}
                      onChange={(e) =>
                        handleStartupChange("eudraTc", e.target.value)
                      }
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong>{t("clinicalTrials.startup.version")}:</strong>
                    <input
                      className="form-control"
                      value={startup?.version || ""}
                      onChange={(e) =>
                        handleStartupChange("version", e.target.value)
                      }
                    />
                  </div>
                </ExpandableContent>

                <ExpandableContent title="Promotor" content="hospital">
                  <div style={{ marginTop: "16px" }}>
                    <strong>{t("clinicalTrials.startup.laboratory")}:</strong>
                    <input
                      className="form-control"
                      defaultValue={startup?.protocol?.laboratory?.title || ""}
                      readOnly
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong>
                      {t("clinicalTrials.startup.sponsorContactLabel")}:
                    </strong>
                    <input
                      className="form-control"
                      value={startup?.sponsorContact || ""}
                      onChange={(e) =>
                        handleStartupChange("sponsorContact", e.target.value)
                      }
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong>
                      {t("clinicalTrials.startup.sponsorContactEmailLabel")}:
                    </strong>
                    <input
                      className="form-control"
                      value={startup?.sponsorContactEmail || ""}
                      onChange={(e) =>
                        handleStartupChange(
                          "sponsorContactEmail",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong>
                      {t(
                        "clinicalTrials.startup.sponsorContactPhoneNumberLabel"
                      )}
                      :
                    </strong>
                    <input
                      className="form-control"
                      value={startup?.sponsorContactPhoneNumber || ""}
                      onChange={(e) =>
                        handleStartupChange(
                          "sponsorContactPhoneNumber",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </ExpandableContent>

                <ExpandableContent
                  title={t("clinicalTrials.startup.cro")}
                  content="hospital"
                >
                  <div style={{ marginTop: "16px" }}>
                    <strong>{t("clinicalTrials.startup.cro")}:</strong>
                    <input
                      className="form-control"
                      value={startup?.cro || ""}
                      onChange={(e) =>
                        handleStartupChange("cro", e.target.value)
                      }
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong>
                      {t("clinicalTrials.startup.croContactLabel")}:
                    </strong>
                    <input
                      className="form-control"
                      value={startup?.croContact || ""}
                      onChange={(e) =>
                        handleStartupChange("croContact", e.target.value)
                      }
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong>
                      {t("clinicalTrials.startup.croContactEmailLabel")}:
                    </strong>
                    <input
                      className="form-control"
                      value={startup?.croContactEmail || ""}
                      onChange={(e) =>
                        handleStartupChange("croContactEmail", e.target.value)
                      }
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong>
                      {t("clinicalTrials.startup.croContactPhoneNumberLabel")}:
                    </strong>
                    <input
                      className="form-control"
                      value={startup?.croContactPhoneNumber || ""}
                      onChange={(e) =>
                        handleStartupChange(
                          "croContactPhoneNumber",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </ExpandableContent>

                <ExpandableContent title="Clasificación" content="notes">
                  <div style={{ marginTop: "16px" }}>
                    <strong>{t("clinicalTrials.startup.indication")}:</strong>
                    <input
                      className="form-control"
                      defaultValue={
                        startup?.protocol?.indication?.description || ""
                      }
                      readOnly
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong>{t("clinicalTrials.startup.keywords")}:</strong>
                    <SnomedPicker
                      conceptType={conceptType.drugs}
                      defaultSelected={startup?.protocol?.keywords ?? []}
                      id="keywords"
                      value={startup?.protocol?.keywords}
                      name="keywords"
                      placeholder={t("commons.search")}
                      multiple={true}
                      onChange={setFieldValue}
                      //onBlur={setFieldTouched}
                      //conceptId={"64572001"} // Trastorno
                      expression={`< 64572001 OR < ${snomedExpressions.Sustancia} OR < ${snomedExpressions.Medicamentos}`} // Trastorno o Sustancia o Producto
                    />
                  </div>
                </ExpandableContent>

                <ExpandableContent
                  title="Responsables"
                  content="person"
                  style={{ marginTop: "24px" }}
                >
                  <div style={{ marginTop: "16px" }}>
                    <strong style={{ display: "block", marginBottom: "4px" }}>
                      {t("clinicalTrials.startup.principalInvestigator")}:
                    </strong>
                    <input
                      className="form-control"
                      defaultValue={
                        startup?.protocol?.principalInvestigator?.fullName || ""
                      }
                      readOnly
                    />
                    <div>
                      {practitionerAttachments &&
                        !!practitionerAttachments[
                          startup?.protocol?.principalInvestigator?.userId
                        ] && (
                          <ul style={{ padding: "0px", marginTop: "5px" }}>
                            {getLatestAttachmentsByCategory(
                              practitionerAttachments[
                                startup?.protocol?.principalInvestigator.userId
                              ]
                            ).map((att, i) => (
                              <li key={i} style={{ display: "flex" }}>
                                <a
                                  href="javascript:void(0);"
                                  onClick={() => handlePreviewFile(att.id)}
                                >
                                  <strong>{att.attachmentCategoryName}</strong>
                                </a>
                                <label style={{ marginLeft: "5px" }}>
                                  Vencimiento:
                                </label>
                                {getAttachmentDueDateStatus(att.dueDate)}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong style={{ display: "block", marginBottom: "4px" }}>
                      {t("clinicalTrials.startup.subInvestigator")}:
                    </strong>
                    <div style={{ marginBottom: "8px" }}>
                      <PractitionerPicker
                        id="subInvestigatorPicker"
                        selected={
                          startup?.protocol?.subInvestigator
                            ? [
                                {
                                  ...startup.protocol.subInvestigator,
                                  fullNameUnique:
                                    startup.protocol.subInvestigator.fullName,
                                },
                              ]
                            : []
                        }
                        onChange={handlePractitionerChange}
                        name="subInvestigator"
                        placeholder={t("commons.select")}
                        isClearable={true}
                        disabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      <CustomSelect
                        value={studyRoles?.find(
                          (x) => x.id == startup?.subInvestigatorRoleId
                        )}
                        onChange={(name, value) =>
                          handleStartupChange(
                            "subInvestigatorRoleId",
                            value?.id
                          )
                        }
                        name="subInvestigatorRole"
                        options={studyRoles}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        placeholder={t("commons.select")}
                        style={{ marginTop: "8px" }}
                        isClearable={true}
                        isDisabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      {practitionerAttachments &&
                        !!practitionerAttachments[
                          startup?.protocol?.subInvestigator?.userId
                        ] && (
                          <ul style={{ padding: "0px", marginTop: "5px" }}>
                            {getLatestAttachmentsByCategory(
                              practitionerAttachments[
                                startup?.protocol?.subInvestigator.userId
                              ]
                            ).map((att, i) => (
                              <li key={i} style={{ display: "flex" }}>
                                <a
                                  href="javascript:void(0);"
                                  onClick={() => handlePreviewFile(att.id)}
                                >
                                  <strong>{att.attachmentCategoryName}</strong>
                                </a>
                                <label style={{ marginLeft: "5px" }}>
                                  Vencimiento:
                                </label>
                                {getAttachmentDueDateStatus(att.dueDate)}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong style={{ display: "block", marginBottom: "4px" }}>
                      {t("clinicalTrials.startup.studyCoordinator")}:
                    </strong>
                    <div style={{ marginBottom: "8px" }}>
                      <PractitionerPicker
                        id="studyCoordinatorPicker"
                        selected={
                          startup?.protocol?.studyCoordinator
                            ? [
                                {
                                  ...startup.protocol.studyCoordinator,
                                  fullNameUnique:
                                    startup.protocol.studyCoordinator.fullName,
                                },
                              ]
                            : []
                        }
                        onChange={handlePractitionerChange}
                        name="studyCoordinator"
                        placeholder={t("commons.select")}
                        isClearable={true}
                        disabled={!isResponsableEditable}
                      />
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <CustomSelect
                        value={studyRoles?.find(
                          (x) => x.id == startup?.studyCoordinatorRoleId
                        )}
                        onChange={(name, value) =>
                          handleStartupChange(
                            "studyCoordinatorRoleId",
                            value?.id
                          )
                        }
                        name="studyCoordinatorRole"
                        options={studyRoles}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        placeholder={t("commons.select")}
                        style={{ marginTop: "8px" }}
                        isClearable={true}
                        isDisabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      {practitionerAttachments &&
                        !!practitionerAttachments[
                          startup?.protocol?.studyCoordinator?.userId
                        ] && (
                          <ul style={{ padding: "0px", marginTop: "5px" }}>
                            {getLatestAttachmentsByCategory(
                              practitionerAttachments[
                                startup?.protocol?.studyCoordinator.userId
                              ]
                            ).map((att, i) => (
                              <li key={i} style={{ display: "flex" }}>
                                <a
                                  href="javascript:void(0);"
                                  onClick={() => handlePreviewFile(att.id)}
                                >
                                  <strong>{att.attachmentCategoryName}</strong>
                                </a>
                                <label style={{ marginLeft: "5px" }}>
                                  Vencimiento:
                                </label>
                                {getAttachmentDueDateStatus(att.dueDate)}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong style={{ display: "block", marginBottom: "4px" }}>
                      {t("clinicalTrials.startup.dataManager")}:
                    </strong>
                    <div style={{ marginBottom: "8px" }}>
                      <PractitionerPicker
                        id="dataManagerPicker"
                        selected={
                          startup?.protocol?.dataManager
                            ? [
                                {
                                  ...startup.protocol.dataManager,
                                  fullNameUnique:
                                    startup.protocol.dataManager.fullName,
                                },
                              ]
                            : []
                        }
                        onChange={handlePractitionerChange}
                        name="dataManager"
                        placeholder={t("commons.select")}
                        isClearable={true}
                        disabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      <CustomSelect
                        value={studyRoles?.find(
                          (x) => x.id == startup?.dataManagerRoleId
                        )}
                        onChange={(name, value) =>
                          handleStartupChange("dataManagerRoleId", value?.id)
                        }
                        name="dataManagerRole"
                        options={studyRoles}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        placeholder={t("commons.select")}
                        style={{ marginTop: "8px" }}
                        isClearable={true}
                        isDisabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      {practitionerAttachments &&
                        !!practitionerAttachments[
                          startup?.protocol?.dataManager?.userId
                        ] && (
                          <ul style={{ padding: "0px", marginTop: "5px" }}>
                            {getLatestAttachmentsByCategory(
                              practitionerAttachments[
                                startup?.protocol?.dataManager.userId
                              ]
                            ).map((att, i) => (
                              <li key={i} style={{ display: "flex" }}>
                                <a
                                  href="javascript:void(0);"
                                  onClick={() => handlePreviewFile(att.id)}
                                >
                                  <strong>{att.attachmentCategoryName}</strong>
                                </a>
                                <label style={{ marginLeft: "5px" }}>
                                  {t(
                                    "clinicalTrials.general.practitionerDocumentDueDate"
                                  )}
                                  :
                                </label>
                                {getAttachmentDueDateStatus(att.dueDate)}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong style={{ display: "block", marginBottom: "4px" }}>
                      {t("clinicalTrials.startup.pharmacist")}:
                    </strong>
                    <div style={{ marginBottom: "8px" }}>
                      <PractitionerPicker
                        id="pharmacistPicker"
                        selected={
                          startup?.protocol?.pharmacist
                            ? [
                                {
                                  ...startup.protocol.pharmacist,
                                  fullNameUnique:
                                    startup.protocol.pharmacist.fullName,
                                },
                              ]
                            : []
                        }
                        onChange={handlePractitionerChange}
                        name="pharmacist"
                        placeholder={t("commons.select")}
                        isClearable={true}
                        disabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      <CustomSelect
                        value={studyRoles?.find(
                          (x) => x.id == startup?.pharmacistRoleId
                        )}
                        onChange={(name, value) =>
                          handleStartupChange("pharmacistRoleId", value?.id)
                        }
                        name="pharmacistRole"
                        options={studyRoles}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        placeholder={t("commons.select")}
                        style={{ marginTop: "8px" }}
                        isClearable={true}
                        isDisabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      {practitionerAttachments &&
                        !!practitionerAttachments[
                          startup?.protocol?.pharmacist?.userId
                        ] && (
                          <ul style={{ padding: "0px", marginTop: "5px" }}>
                            {getLatestAttachmentsByCategory(
                              practitionerAttachments[
                                startup?.protocol?.pharmacist.userId
                              ]
                            ).map((att, i) => (
                              <li key={i} style={{ display: "flex" }}>
                                <a
                                  href="javascript:void(0);"
                                  onClick={() => handlePreviewFile(att.id)}
                                >
                                  <strong>{att.attachmentCategoryName}</strong>
                                </a>
                                <label style={{ marginLeft: "5px" }}>
                                  {t(
                                    "clinicalTrials.general.practitionerDocumentDueDate"
                                  )}
                                  :
                                </label>
                                {getAttachmentDueDateStatus(att.dueDate)}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong style={{ display: "block", marginBottom: "4px" }}>
                      {t("clinicalTrials.startup.nursing")}:
                    </strong>
                    <div style={{ marginBottom: "8px" }}>
                      <PractitionerPicker
                        id="nurcingPicker"
                        selected={
                          startup?.protocol?.nurcing
                            ? [
                                {
                                  ...startup.protocol.nurcing,
                                  fullNameUnique:
                                    startup.protocol.nurcing.fullName,
                                },
                              ]
                            : []
                        }
                        onChange={handlePractitionerChange}
                        name="nurcing"
                        placeholder={t("commons.select")}
                        isClearable={true}
                        disabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      <CustomSelect
                        value={studyRoles?.find(
                          (x) => x.id == startup?.nurcingRoleId
                        )}
                        onChange={(name, value) =>
                          handleStartupChange("nurcingRoleId", value?.id)
                        }
                        name="nurcingRole"
                        options={studyRoles}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        placeholder={t("commons.select")}
                        style={{ marginTop: "8px" }}
                        isClearable={true}
                        isDisabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      {practitionerAttachments &&
                        !!practitionerAttachments[
                          startup?.protocol?.nurcing?.userId
                        ] && (
                          <ul style={{ padding: "0px", marginTop: "5px" }}>
                            {getLatestAttachmentsByCategory(
                              practitionerAttachments[
                                startup?.protocol?.nurcing.userId
                              ]
                            ).map((att, i) => (
                              <li key={i} style={{ display: "flex" }}>
                                <a
                                  href="javascript:void(0);"
                                  onClick={() => handlePreviewFile(att.id)}
                                >
                                  <strong>{att.attachmentCategoryName}</strong>
                                </a>
                                <label style={{ marginLeft: "5px" }}>
                                  {t(
                                    "clinicalTrials.general.practitionerDocumentDueDate"
                                  )}
                                  :
                                </label>
                                {getAttachmentDueDateStatus(att.dueDate)}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong style={{ display: "block", marginBottom: "4px" }}>
                      {t("clinicalTrials.startup.administrator")}:
                    </strong>
                    <div style={{ marginBottom: "8px" }}>
                      <PractitionerPicker
                        id="administratorPicker"
                        selected={
                          startup?.protocol?.administrator
                            ? [
                                {
                                  ...startup.protocol.administrator,
                                  fullNameUnique:
                                    startup.protocol.administrator.fullName,
                                },
                              ]
                            : []
                        }
                        onChange={handlePractitionerChange}
                        name="administrator"
                        placeholder={t("commons.select")}
                        isClearable={true}
                        disabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      <CustomSelect
                        value={studyRoles?.find(
                          (x) => x.id == startup?.administratorRoleId
                        )}
                        onChange={(name, value) =>
                          handleStartupChange("administratorRoleId", value?.id)
                        }
                        name="administratorRole"
                        options={studyRoles}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        placeholder={t("commons.select")}
                        style={{ marginTop: "8px" }}
                        isClearable={true}
                        isDisabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      {practitionerAttachments &&
                        !!practitionerAttachments[
                          startup?.protocol?.administrator?.userId
                        ] && (
                          <ul style={{ padding: "0px", marginTop: "5px" }}>
                            {getLatestAttachmentsByCategory(
                              practitionerAttachments[
                                startup?.protocol?.administrator.userId
                              ]
                            ).map((att, i) => (
                              <li key={i} style={{ display: "flex" }}>
                                <a
                                  href="javascript:void(0);"
                                  onClick={() => handlePreviewFile(att.id)}
                                >
                                  <strong>{att.attachmentCategoryName}</strong>
                                </a>
                                <label style={{ marginLeft: "5px" }}>
                                  {t(
                                    "clinicalTrials.general.practitionerDocumentDueDate"
                                  )}
                                  :
                                </label>
                                {getAttachmentDueDateStatus(att.dueDate)}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong style={{ display: "block", marginBottom: "4px" }}>
                      {t("clinicalTrials.startup.monitor")}:
                    </strong>
                    <div style={{ marginBottom: "8px" }}>
                      <PractitionerPicker
                        id="monitorPicker"
                        selected={
                          startup?.protocol?.monitor
                            ? [
                                {
                                  ...startup.protocol.monitor,
                                  fullNameUnique:
                                    startup.protocol.monitor.fullName,
                                },
                              ]
                            : []
                        }
                        onChange={handlePractitionerChange}
                        name="monitor"
                        placeholder={t("commons.select")}
                        isClearable={true}
                        disabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      <CustomSelect
                        value={studyRoles?.find(
                          (x) => x.id == startup?.monitorRoleId
                        )}
                        onChange={(name, value) =>
                          handleStartupChange("monitorRoleId", value?.id)
                        }
                        name="monitorRole"
                        options={studyRoles}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        placeholder={t("commons.select")}
                        style={{ marginTop: "8px" }}
                        isClearable={true}
                        isDisabled={!isResponsableEditable}
                      />
                    </div>
                    <div>
                      {practitionerAttachments &&
                        !!practitionerAttachments[
                          startup?.protocol?.monitor?.userId
                        ] && (
                          <ul style={{ padding: "0px", marginTop: "5px" }}>
                            {getLatestAttachmentsByCategory(
                              practitionerAttachments[
                                startup?.protocol?.monitor.userId
                              ]
                            ).map((att, i) => (
                              <li key={i} style={{ display: "flex" }}>
                                <a
                                  href="javascript:void(0);"
                                  onClick={() => handlePreviewFile(att.id)}
                                >
                                  <strong>{att.attachmentCategoryName}</strong>
                                </a>
                                <label style={{ marginLeft: "5px" }}>
                                  {t(
                                    "clinicalTrials.general.practitionerDocumentDueDate"
                                  )}
                                  :
                                </label>
                                {getAttachmentDueDateStatus(att.dueDate)}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </div>
                </ExpandableContent>

                <ExpandableContent
                  style={{ display: "block", marginBottom: "4px" }}
                  title="Detalles del Ensayo"
                  content="folder"
                >
                  <div style={{ marginTop: "16px" }}>
                    <strong style={{ display: "block", marginBottom: "4px" }}>
                      {t("clinicalTrials.startup.trialType")}:
                    </strong>
                    <CreatableSelect
                      classNamePrefix="react-select"
                      value={{
                        label: startup?.trialType || "",
                        value: startup?.trialType || "",
                      }}
                      options={trialTypeOptions}
                      onChange={(selectedOption) =>
                        handleStartupChange("trialType", selectedOption?.value)
                      }
                      isClearable
                      placeholder={t(
                        "clinicalTrials.startup.trialTypePlaceholder"
                      )}
                    />
                  </div>
                  <div style={{ marginTop: "16px" }}>
                    <strong style={{ display: "block", marginBottom: "4px" }}>
                      {t("clinicalTrials.startup.trialTypeDescriptionLabel")}:
                    </strong>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={startup?.trialTypeDescription || ""}
                      onChange={(e) =>
                        handleStartupChange(
                          "trialTypeDescription",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </ExpandableContent>
              </>
            }
          />
        </div>
      </Col>
      <Col md={8}>
        {(!isProtocolActivated ||
          (isProtocolActivated && !showActivateCenterPanel)) && (
          <>
            <Row>
              <Col md={6}>
                <Card
                  content={
                    <DocumentUploadCard
                      attachmentEntityTypeId={attachmentEntityType.Startup}
                      entityId={startup?.id}
                      protocolId={startup?.protocol?.id}
                      attachment={startup?.attachments.find(
                        (x) =>
                          x.attachmentCategoryId === attachmentCategories.cda
                      )}
                      legend={t("clinicalTrials.general.documentLegends.cda")}
                      onAttachmentChange={handleAttachmentChange}
                      showDownloadButton={false}
                      disabled={!isAuthorized(["Startups.Edit"])}
                    />
                  }
                />
              </Col>
              <Col md={6}>
                <Card
                  content={
                    <DocumentUploadCard
                      attachmentEntityTypeId={attachmentEntityType.Startup}
                      entityId={startup?.id}
                      protocolId={startup?.protocol?.id}
                      attachment={startup?.attachments.find(
                        (x) =>
                          x.attachmentCategoryId ===
                          attachmentCategories.feasibility
                      )}
                      legend={t(
                        "clinicalTrials.general.documentLegends.feasibility"
                      )}
                      onAttachmentChange={handleAttachmentChange}
                      disabled={!isAuthorized(["Startups.Edit"])}
                    />
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Card
                  content={
                    <div>
                      <div
                        className="form-check form-switch"
                        style={{
                          gap: "12px",
                          minHeight: "40px",
                          alignItems: "center",
                          display: "flex",
                        }}
                      >
                        <CustomSwitch
                          name={"is-competitive"}
                          value={getSwitchValue("is-competitive")}
                          onChange={(name, val) =>
                            handleSwitchChange(name, val)
                          }
                        />
                        <label
                          className="form-check-label mb-0"
                          htmlFor={"is-competitive"}
                        >
                          {t("clinicalTrials.startup.competitive")}

                          {getSwitchValue("is-competitive") && (
                            <a
                              href="#"
                              style={{ marginLeft: "10px" }}
                              onClick={(e) => {
                                e.preventDefault();
                                setShowCompetitors(true);
                              }}
                            >
                              {t(
                                "clinicalTrials.general.show-competitive-label"
                              )}
                            </a>
                          )}
                        </label>
                      </div>
                    </div>
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Card
                  content={
                    <>
                      <Row>
                        <Col md={12}>
                          <div>
                            <div
                              className="form-check form-switch d-flex align-items-center"
                              style={{
                                gap: "12px",
                                minHeight: "40px",
                                alignItems: "center",
                                display: "flex",
                              }}
                            >
                              <CustomSwitch
                                name={"is-pre-selection"}
                                value={getSwitchValue("is-pre-selection")}
                                onChange={(name, val) =>
                                  handleSwitchChange(name, val)
                                }
                              />
                              <label
                                className="form-check-label mb-0"
                                htmlFor={"is-pre-selection"}
                              >
                                {t(`clinicalTrials.startup.preselectionStage`)}
                              </label>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      {startup?.preselectionStage && (
                        <Row>
                          <Col md={6}>
                            <div style={{ marginTop: "10px" }}>
                              <label>
                                {t("clinicalTrials.startup.preselectionDate")}
                              </label>
                              <CustomDate
                                dateFormat="DD/MM/YYYY"
                                timeFormat={false}
                                inputProps={{ placeholder: "DD/MM/AAAA" }}
                                value={startup?.preselectionDate}
                                name="preselectionDate"
                                onChange={handleStartupChange}
                                //onBlur={setFieldTouched}
                                viewMode="years"
                                closeOnSelect={true}
                              />
                            </div>
                          </Col>
                        </Row>
                      )}
                    </>
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Card
                  content={
                    <>
                      <Row>
                        <Col md={12}>
                          <div>
                            <div
                              className="form-check form-switch d-flex align-items-center"
                              style={{
                                gap: "12px",
                                minHeight: "40px",
                                alignItems: "center",
                                display: "flex",
                              }}
                            >
                              <CustomSwitch
                                name={"is-selection"}
                                value={getSwitchValue("is-selection")}
                                onChange={(name, val) =>
                                  handleSwitchChange(name, val)
                                }
                              />
                              <label
                                className="form-check-label mb-0"
                                htmlFor={"is-selection"}
                              >
                                {t(`clinicalTrials.startup.selectionStage`)}
                              </label>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      {startup?.selectionStage && (
                        <>
                          <Row>
                            <Col md={6}>
                              <div style={{ marginTop: "10px" }}>
                                <label>
                                  {t("clinicalTrials.startup.selectionDate")}
                                </label>
                                <CustomDate
                                  dateFormat="DD/MM/YYYY"
                                  timeFormat={false}
                                  inputProps={{ placeholder: "DD/MM/AAAA" }}
                                  value={startup?.selectionDate}
                                  name="selectionDate"
                                  onChange={handleStartupChange}
                                  //onBlur={setFieldTouched}
                                  viewMode="years"
                                  closeOnSelect={true}
                                />
                              </div>
                            </Col>
                            <Col md={6}>
                              <div style={{ marginTop: "10px" }}>
                                <label>
                                  {t("clinicalTrials.startup.visitType")}
                                </label>
                                <CustomSelect
                                  value={visitTypeSelectedOption}
                                  name="visitType"
                                  onChange={(name, selected) => {
                                    setVisitTypeSelectedOption(selected);
                                    setStartup((prev) => ({
                                      ...prev,
                                      [name]: selected?.value,
                                    }));
                                  }}
                                  isMulti={false}
                                  isClearable={true}
                                  loading={false}
                                  options={optionsSelection}
                                  getOptionLabel={(option) => option.label}
                                  getOptionValue={(option) => option.value}
                                />
                              </div>
                            </Col>
                          </Row>
                          <Row style={{ marginTop: "10px" }}>
                            <Col md={6}>
                              <DocumentUploadCard
                                attachmentEntityTypeId={
                                  attachmentEntityType.Startup
                                }
                                entityId={startup?.id}
                                protocolId={startup?.protocol?.id}
                                attachment={startup?.attachments.find(
                                  (x) =>
                                    x.attachmentCategoryId ===
                                    attachmentCategories.communicationFile
                                )}
                                legend={""}
                                onAttachmentChange={handleAttachmentChange}
                                disabled={!isAuthorized(["Startups.Edit"])}
                              />
                            </Col>
                            <Col md={6}>
                              <DocumentUploadCard
                                attachmentEntityTypeId={
                                  attachmentEntityType.Startup
                                }
                                entityId={startup?.id}
                                protocolId={startup?.protocol?.id}
                                attachment={startup?.attachments.find(
                                  (x) =>
                                    x.attachmentCategoryId ===
                                    attachmentCategories.selectionVisitType
                                )}
                                legend={""}
                                onAttachmentChange={handleAttachmentChange}
                                disabled={!isAuthorized(["Startups.Edit"])}
                              />
                            </Col>
                          </Row>
                        </>
                      )}
                    </>
                  }
                />
              </Col>
            </Row>

            {!isProtocolActivated && (
              <Row>
                <Col md={12}>
                  <Card
                    content={
                      <div className="p-3 border rounded">
                        <h6>
                          <strong>
                            {t(
                              "clinicalTrials.general.participantAcceptanceLabel"
                            )}
                          </strong>
                        </h6>
                        <div className="text-muted mb-2">
                          {t(
                            "clinicalTrials.general.participantAcceptanceStatusLabel"
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "24px",
                          }}
                        >
                          {["Proposal", "StartUp", "Discarded"].map((opt) => (
                            <div
                              key={opt}
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <CustomRadio
                                number={`status-${opt}`}
                                name="status"
                                label={t(`clinicalTrials.statuses.${opt}`)}
                                option={opt}
                                disabled={
                                  isMonitor ||
                                  startup?.protocol?.status?.code === opt
                                }
                                checked={
                                  startup?.protocol?.status?.code === opt
                                }
                                onChange={handleStatusChange}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    }
                  />
                </Col>
              </Row>
            )}
          </>
        )}

        {(startup?.protocol?.status?.code === "StartUp" ||
          (isProtocolActivated && !showActivateCenterPanel)) && (
          <AcceptState
            activateCenter={handleActivateCenter}
            setStartup={setStartup}
            startup={startup}
            handleAttachmentChange={handleAttachmentChange}
          />
        )}

        {startup?.protocol?.status?.code === "Discarded" && (
          <DiscardedState
            setStartup={setStartup}
            startup={startup}
            handleAttachmentChange={handleAttachmentChange}
          />
        )}

        {isProtocolActivated && showActivateCenterPanel && (
          <ActivateCenter
            setStartup={setStartup}
            startup={startup}
            handleAttachmentChange={handleAttachmentChange}
            onSaveStartup={(newState) => {
              onSaveStartup(newState, () => {
                setShowActivateCenterPanel(true);
              });

              onChangeStatus && onChangeStatus(newState);
            }}
          />
        )}
      </Col>
      <Modal
        show={!!showCompetitors}
        onHide={() => {
          setShowCompetitors(false);
        }}
        bsSize="large"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("clinicalTrials.general.competitive-modal-header")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!clinicalTrialsCompetitors && <Spinner />}
          {clinicalTrialsCompetitors && (
            <Card
              content={
                <BootstrapTable
                  bordered={false}
                  hover
                  remote
                  keyField="id"
                  data={clinicalTrialsCompetitors}
                  columns={competitorsTableColumns}
                  classes="table-spaced table-spaced-small"
                  wrapperClasses="table-responsive"
                  noDataIndication={() => <>{t("commons.noData")}</>}
                />
              }
            />
          )}
        </Modal.Body>
      </Modal>
      <DocumentViewer
        file={response?.blob}
        fileName={response?.fileName}
        inModal={true}
        isLoading={false}
      />
      {showDuplicatedModal && (
        <ModalResponsibleDuplicated
          show={showDuplicatedModal}
          onClose={() => setShowDuplicatedModal(false)}
        />
      )}
    </Row>
  );
};

export default General;
